import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  accountMode: string;
  subscription?: {
    status: string;
    plan: string;
    trialEndsAt?: any;
  };
  roles?: string[];
  contractorId?: string;
  orgName?: string; // Organization name from orgs collection
  isAdmin?: boolean;
  isOwner?: boolean;
  isMember?: boolean;
  isPrimaryAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasActiveSubscription: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isAuthenticated: false,
  hasActiveSubscription: false,
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const profileUnsubscribe = useRef<(() => void) | null>(null);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Set up real-time listener for user profile
        subscribeToProfile(firebaseUser.uid);
      } else {
        // Clean up profile subscription
        if (profileUnsubscribe.current) {
          profileUnsubscribe.current();
          profileUnsubscribe.current = null;
        }
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (profileUnsubscribe.current) {
        profileUnsubscribe.current();
      }
    };
  }, []);

  const subscribeToProfile = useCallback((uid: string) => {
    // Clean up existing subscription
    if (profileUnsubscribe.current) {
      profileUnsubscribe.current();
    }

    // Subscribe to real-time updates
    const userDocRef = doc(db, 'users', uid);
    profileUnsubscribe.current = onSnapshot(
      userDocRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Fetch roles and org info in parallel
          let roles: string[] = [];
          let contractorId = uid;
          let orgName: string | undefined;
          let isPrimaryAdmin = false;

          try {
            const rolesDoc = await getDoc(doc(db, 'user_roles', uid));
            if (rolesDoc.exists()) {
              const rolesData = rolesDoc.data();
              roles = rolesData.roles || [rolesData.role].filter(Boolean);
              contractorId = rolesData.contractorId || uid;
            }
          } catch (e) {
            // Roles not found, user is likely a contractor (owner)
          }

          // Fetch organization name from the user's orgId field or contractorId
          try {
            // Check if user has orgId directly in their document (org users from admin invite)
            const userOrgId = data.orgId;
            if (userOrgId) {
              const orgDoc = await getDoc(doc(db, 'orgs', userOrgId));
              if (orgDoc.exists()) {
                orgName = orgDoc.data().name;
                contractorId = userOrgId; // Ensure contractorId is the orgId
              }
              // Check if user is primary admin from the org document
              const orgData = orgDoc.exists() ? orgDoc.data() : null;
              if (orgData?.primaryAdminId === uid) {
                isPrimaryAdmin = true;
              }
            } else if (contractorId && contractorId.startsWith('org_')) {
              // Fallback: check if the contractorId is an org
              const orgDoc = await getDoc(doc(db, 'orgs', contractorId));
              if (orgDoc.exists()) {
                orgName = orgDoc.data().name;
              }
            }
          } catch (e) {
            // Org not found, user is likely a personal account
          }

          // Determine role flags based on org roles (owner, admin, member)
          const isOwner = roles.includes('owner') || isPrimaryAdmin;
          const isAdmin = roles.includes('admin') || data.accountMode === 'contractor' || isOwner;
          const isMember = roles.includes('member');

          const profileData = {
            uid,
            email: data.email || auth.currentUser?.email,
            displayName: data.name || data.displayName || auth.currentUser?.displayName,
            photoURL: data.avatarUrl || data.photoURL || auth.currentUser?.photoURL,
            accountMode: data.accountMode || data.account_mode || 'contractor',
            subscription: data.subscription,
            roles,
            contractorId,
            orgName,
            isAdmin,
            isOwner,
            isMember,
            isPrimaryAdmin,
          };
          setProfile(profileData);
        } else {
          // Create a fallback profile from auth data so the app doesn't get stuck
          // This handles cases where the Firestore document was deleted or never created
          const currentUser = auth.currentUser;
          if (currentUser) {
            setProfile({
              uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              photoURL: currentUser.photoURL,
              accountMode: 'personal', // Default to personal account
              roles: [],
              contractorId: uid, // Use their own uid as contractorId
              isAdmin: true, // Give them admin access to their own data
              isOwner: true,
              isMember: false,
              isPrimaryAdmin: true,
            });
          }
        }
        setLoading(false);
      },
      (error) => {
        console.error('[AuthContext] Error listening to profile:', error);
        setLoading(false);
      }
    );
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      subscribeToProfile(user.uid);
    }
  }, [user, subscribeToProfile]);

  const hasActiveSubscription = Boolean(
    profile?.subscription?.status === 'active' ||
    profile?.subscription?.status === 'trial' ||
    sessionStorage.getItem('subscription_active') ||
    sessionStorage.getItem('just_completed_payment')
  );

  const value: AuthContextType = {
    user,
    profile,
    loading,
    isAuthenticated: !!user,
    hasActiveSubscription,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
